<template>
  <div class="car-form-wrap">
    <FormCard :title="mode === 'edit' ? 'Данные авто' : 'Автомобиль'" source="carrier">
      <FormGrid :cols="singleColumn ? 1 : 2">
        <FormField
          label="Номер"
          :invalid="(!form.number.valid && form.number.tochted) || !!errors.number"
          :error="errors.number || fieldError(form.number)"
        >
          <input
            id="car-number"
            v-model.trim="form.number.value"
            type="text"
            :class="{ invalid: (!form.number.valid && form.number.tochted) || errors.number }"
            @blur="form.number.blur"
          />
        </FormField>
        <FormField
          label="VIN"
          :invalid="(!form.vin.valid && form.vin.tochted) || !!errors.vin"
          :error="errors.vin || fieldError(form.vin)"
        >
          <input
            id="car-vin"
            v-model.trim="form.vin.value"
            type="text"
            :class="{ invalid: (!form.vin.valid && form.vin.tochted) || errors.vin }"
            @blur="form.vin.blur"
          />
        </FormField>
        <FormField
          label="IMEI"
          help="15 цифр, если указан"
          :invalid="(!form.imei.valid && form.imei.tochted) || !!errors.imei"
          :error="errors.imei || fieldError(form.imei)"
        >
          <input
            id="car-imei"
            v-model.trim="form.imei.value"
            type="text"
            inputmode="numeric"
            maxlength="20"
            placeholder="только цифры"
            @blur="form.imei.blur"
          />
        </FormField>
        <FormField
          label="Тип собственности"
          :invalid="ownershipTouched && !ownershipCode"
          :error="ownershipTouched && !ownershipCode ? 'Укажите тип собственности' : ''"
        >
          <select id="car-ownership" v-model="ownershipCode">
            <option value="">Не указан</option>
            <option
              v-for="opt in ownershipOptions"
              :key="opt.value"
              :value="opt.code"
            >
              {{ opt.name }}
            </option>
          </select>
        </FormField>

        <template v-if="isAttracted">
          <FormField
            label="Тип собственности перевозчика"
            help="при привлечённом ТС доступен только «1 - Собственность»"
          >
            <select
              id="car-carrier-ownership"
              v-model="carrierOwnershipCode"
            >
              <option
                v-for="opt in carrierOptions"
                :key="'carrier-' + opt.value"
                :value="opt.code"
              >
                {{ opt.name }}
              </option>
            </select>
          </FormField>
          <FormField
            label="Конечный перевозчик"
            :invalid="attractedInvalid && !form.final_carrier_name.value"
            :error="errors.final_carrier_name"
          >
            <input
              id="car-final-carrier-name"
              v-model.trim="form.final_carrier_name.value"
              type="text"
              @input="markFinalNameEdited"
              @blur="form.final_carrier_name.blur"
            />
            <small v-if="filledFromCompany.name" class="autofill-hint">из компании, можно изменить</small>
          </FormField>
          <FormField
            label="ИНН конечного перевозчика"
            :invalid="attractedInvalid && !form.final_carrier_inn.value"
            :error="errors.final_carrier_inn"
          >
            <input
              id="car-final-carrier-inn"
              v-model.trim="form.final_carrier_inn.value"
              type="text"
              inputmode="numeric"
              maxlength="12"
              @input="markFinalInnEdited"
              @blur="form.final_carrier_inn.blur"
            />
            <small v-if="filledFromCompany.inn" class="autofill-hint">из компании, можно изменить</small>
          </FormField>
        </template>

        <FormField label="Глонасс">
          <input
            id="car-glonass"
            v-model="form.glonass.value"
            type="checkbox"
          />
        </FormField>
        <FormField label="Черный список">
          <input
            id="car-blacklist"
            v-model="form.blackList.value"
            type="checkbox"
            @blur="form.blackList.blur"
          />
        </FormField>
        <FormField v-if="!noTrailer && mode !== 'edit'" label="С прицепом">
          <input
            id="car-with-trailer"
            v-model="trailer"
            type="checkbox"
          />
        </FormField>
      </FormGrid>

      <p v-if="isAttracted && !hasCompanyDefaults && mode !== 'edit'" class="autofill-hint autofill-warn">
        Компания не выбрана — укажите конечного перевозчика вручную
      </p>
      <p v-if="formError" class="form-error">{{ formError }}</p>

      <div class="form-actions">
        <my-shrunk-button
          @click="sendForm"
          :btnSettings="{shrunk:true,name:''}"
        >{{ mode === 'edit' ? 'Сохранить' : 'Создать' }}</my-shrunk-button>
        <my-shrunk-button
          class="cancel-btn bg-denied"
          @click="closeForm"
          :btnSettings="{shrunk:true,name:''}"
        >Отменить</my-shrunk-button>
      </div>
    </FormCard>

    <TrailerForm
      v-if="trailer && !noTrailer && mode !== 'edit'"
      ref="formTrailer"
      :noButton="true"
      :single-column="singleColumn"
    />
  </div>
</template>


<script>
import { computed, ref, watch } from 'vue'
import { useForm, useFormat, touchForm } from '@/hooks/form'
import TrailerForm from '@/components/forms/TrailerForm'
import {
  CAR_OWNERSHIP_OPTIONS,
  OWNERSHIP_ATTRACTED,
  OWNERSHIP_OWNED,
  buildCarCreatePayload,
  extractCompanyCarrierDefaults,
  findOwnershipOption,
  resolveAttractedCarrierAutofill,
  validateCarCreateForm,
} from '@/constants/carOwnership'

function initialOwnershipCode(car) {
  const byId = findOwnershipOption(car?.ownership_type_id)
  if (byId) return byId.code
  const byName = findOwnershipOption(car?.ownership_type_name)
  if (byName) return byName.code
  const obj = car?.ownership_type
  if (obj && (obj.value || obj.name)) {
    const opt = findOwnershipOption(obj.value) || findOwnershipOption(obj.name)
    if (opt) return opt.code
  }
  return ''
}

function initialCarrierOwnershipCode(car) {
  const byId = findOwnershipOption(car?.carrier_ownership_type_id)
  if (byId) return byId.code
  const byName = findOwnershipOption(car?.carrier_ownership_type_name)
  if (byName) return byName.code
  const obj = car?.carrier_ownership_type
  if (obj && (obj.value || obj.name)) {
    const opt = findOwnershipOption(obj.value) || findOwnershipOption(obj.name)
    if (opt) return opt.code
  }
  return OWNERSHIP_OWNED.code
}

export default {
  components: {
    TrailerForm,
  },
  name: 'CarForm',
  props: {
    company: {
      type: Object,
      default: null,
    },
    noTrailer: {
      type: Boolean,
      default: false,
    },
    mode: { type: String, default: 'create' },
    initialData: { type: Object, default: () => ({}) },
    errors: { type: Object, default: () => ({}) },
    singleColumn: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const isEdit = computed(() => props.mode === 'edit')
    const trailer = ref(true)
    const formTrailer = ref({})
    const formError = ref('')
    const ownershipOptions = CAR_OWNERSHIP_OPTIONS
    const carrierOptions = [OWNERSHIP_OWNED]
    const filledFromCompany = ref({ name: false, inn: false })

    const ownershipCode = ref('')
    const carrierOwnershipCode = ref(OWNERSHIP_OWNED.code)
    const ownershipTouched = ref(false)

    const form = useForm({
      number: {
        value: '',
        validators: ['required'],
      },
      vin: {
        value: '',
        validators: ['required', 'vin'],
      },
      imei: {
        value: '',
        validators: ['imei'],
      },
      glonass: {
        value: false,
        validators: [],
      },
      final_carrier_name: {
        value: '',
        validators: [],
      },
      final_carrier_inn: {
        value: '',
        validators: ['inn'],
      },
      blackList: {
        value: false,
        validators: [],
      },
    })

    const fillInitialData = (source = {}) => {
      const src = source?.car || source || {}
      if (!isEdit.value) return
      if (src.number != null) form.number.value = src.number
      if (src.vin != null) form.vin.value = src.vin
      if (src.imei != null) form.imei.value = src.imei
      if (src.glonass != null) form.glonass.value = Boolean(src.glonass)
      if (src.final_carrier_name != null) form.final_carrier_name.value = src.final_carrier_name
      if (src.final_carrier_inn != null) form.final_carrier_inn.value = src.final_carrier_inn
      form.blackList.value = Boolean(
        src.blackList ?? src.black_list ?? src.security_info?.black_list ?? false,
      )
      ownershipCode.value = initialOwnershipCode(src)
      carrierOwnershipCode.value = initialCarrierOwnershipCode(src) || OWNERSHIP_OWNED.code
    }
    watch([isEdit, () => props.initialData], ([edit, data]) => {
      if (edit) fillInitialData(data)
    }, { immediate: true })

    const isAttracted = computed(
      () => ownershipCode.value === OWNERSHIP_ATTRACTED.code,
    )
    const attractedInvalid = computed(() => Boolean(formError.value) && isAttracted.value)
    const hasCompanyDefaults = computed(() => {
      const defaults = extractCompanyCarrierDefaults(props.company)
      return Boolean(defaults.final_carrier_name || defaults.final_carrier_inn)
    })

    function applyCompanyAutofill() {
      if (!isAttracted.value) return
      const next = resolveAttractedCarrierAutofill({
        company: props.company,
        currentName: form.final_carrier_name.value,
        currentInn: form.final_carrier_inn.value,
        filledFromCompany: filledFromCompany.value,
      })
      form.final_carrier_name.value = next.final_carrier_name
      form.final_carrier_inn.value = next.final_carrier_inn
      filledFromCompany.value = next.filledFromCompany
    }

    function clearAutofilledCarrierFields() {
      if (filledFromCompany.value.name) {
        form.final_carrier_name.value = ''
      }
      if (filledFromCompany.value.inn) {
        form.final_carrier_inn.value = ''
      }
      filledFromCompany.value = { name: false, inn: false }
    }

    function markFinalNameEdited() {
      filledFromCompany.value = { ...filledFromCompany.value, name: false }
    }

    function markFinalInnEdited() {
      filledFromCompany.value = { ...filledFromCompany.value, inn: false }
    }

    watch(ownershipCode, (code, prev) => {
      if (code === OWNERSHIP_ATTRACTED.code) {
        applyCompanyAutofill()
        return
      }
      if (prev === OWNERSHIP_ATTRACTED.code && !isEdit.value) {
        clearAutofilledCarrierFields()
      }
    })

    watch(
      () => [props.company?.id, props.company?.name, props.company?.inn],
      () => applyCompanyAutofill(),
    )

    if (isEdit.value && isAttracted.value) applyCompanyAutofill()

    const fieldError = (field) => field.tochted
      ? Object.values(field.errors).find(Boolean) || ''
      : ''

    const sendForm = async () => {
      formError.value = ''
      ownershipTouched.value = true
      const isFormValid = await touchForm(form)

      if (!ownershipCode.value) {
        formError.value = 'Укажите тип собственности'
        return
      }

      if (!isFormValid) {
        formError.value = 'Заполните номер и VIN'
        return
      }

      const flat = {
        ...useFormat(form),
        ownershipCode: ownershipCode.value,
        carrierOwnershipCode: isAttracted.value ? carrierOwnershipCode.value : '',
      }
      delete flat.blackList

      if (!isAttracted.value) {
        delete flat.final_carrier_name
        delete flat.final_carrier_inn
      }

      const validationError = validateCarCreateForm(flat)
      if (validationError) {
        formError.value = validationError
        return
      }

      const sendFormData = buildCarCreatePayload(flat)

      if (isEdit.value) {
        if (props.initialData?.id != null) sendFormData.id = props.initialData.id
        if (props.initialData?.is_trailer != null) {
          sendFormData.is_trailer = Boolean(props.initialData.is_trailer)
        }
        emit('update', sendFormData)
        return
      }

      if (trailer.value && !props.noTrailer) {
        const hasTrailerData = ['number', 'vin'].some((key) =>
          String(formTrailer.value.form?.[key]?.value || '').trim(),
        )
        const trailerData = hasTrailerData ? await formTrailer.value.getFormObject() : null
        if (hasTrailerData && !trailerData) {
          formError.value = 'Исправьте данные прицепа'
          return
        }
        if (trailerData) {
          sendFormData.trailer = trailerData
        }
      }
      emit('sendForm', { model: 'cars', form: sendFormData })
    }

    const closeForm = () => {
      emit('closeForm')
    }

    return {
      closeForm,
      formTrailer,
      trailer,
      form,
      formError,
      fieldError,
      ownershipCode,
      ownershipTouched,
      carrierOwnershipCode,
      ownershipOptions,
      carrierOptions,
      isAttracted,
      attractedInvalid,
      hasCompanyDefaults,
      filledFromCompany,
      markFinalNameEdited,
      markFinalInnEdited,
      sendForm,
      mode: props.mode,
      errors: props.errors,
    }
  },
}
</script>


<style scoped>
.car-form-wrap {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.form-actions {
  display: flex;
  gap: 20px;
  margin-top: 12px;
}

.cancel-btn {
  margin-left: 0;
}

.form-error {
  color: #c62828;
  margin: 8px 0 12px;
  font-size: 13px;
}

.autofill-hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.autofill-warn {
  color: #b26a00;
  margin: 0 0 8px;
}
</style>
